import { useRef } from 'react';

type AutoFocusHandler = (event: Event) => void;

/**
 * Ouverte depuis un élément de menu (menu d'actions d'une ligne), la fenêtre
 * rend le focus au bouton qui ouvrait ce menu : l'élément de menu lui-même
 * disparaît avec le menu.
 */
function resolveOpener(active: Element | null): HTMLElement | null {
  if (!(active instanceof HTMLElement)) {
    return null;
  }
  const menu = active.closest<HTMLElement>('[role="menu"]');
  if (menu?.id) {
    const trigger = document.querySelector<HTMLElement>(`[aria-controls="${CSS.escape(menu.id)}"]`);
    if (trigger) {
      return trigger;
    }
  }
  return active;
}

/**
 * Rend le focus à l'élément actif au moment de l'ouverture d'un `Dialog` ou
 * d'un `Sheet`. Radix ne le fait que via `Dialog.Trigger` ; or la plupart des
 * fenêtres de l'app sont pilotées (`open`/`onOpenChange`) depuis un bouton ou un
 * menu de ligne — sans ce relais, le focus retombait sur `body` à la fermeture.
 * Un gestionnaire passé par l'appelant reste prioritaire (`preventDefault`).
 */
export function useReturnFocus(
  onOpenAutoFocus: AutoFocusHandler | undefined,
  onCloseAutoFocus: AutoFocusHandler | undefined,
): { onOpenAutoFocus: AutoFocusHandler; onCloseAutoFocus: AutoFocusHandler } {
  const openerRef = useRef<HTMLElement | null>(null);

  return {
    onOpenAutoFocus: (event) => {
      openerRef.current = resolveOpener(document.activeElement);
      onOpenAutoFocus?.(event);
    },
    onCloseAutoFocus: (event) => {
      onCloseAutoFocus?.(event);
      const opener = openerRef.current;
      if (event.defaultPrevented || !opener?.isConnected || opener === document.body) {
        return;
      }
      event.preventDefault();
      opener.focus();
    },
  };
}
